export interface User {
  id: string;
  handle: string;
  display_name: string;
  bio: string;
  avatar_url: string;
  links: { label: string; url: string }[];
  email: string;
  profile_visibility: "private" | "members" | "public";
  locale: string;
  currency: string;
  is_admin: boolean;
  role: "owner" | "admin" | "member";
  ai_tier: string;
  tenant: { id: string; slug: string; name: string };
  created_at: string;
  cover_url: string;
  contacts: Contact[];
  avatar_crop: Crop | null;
  cover_crop: Crop | null;
  has_avatar_source: boolean;
  has_cover_source: boolean;
}

/** ADR-0019: each contact detail has its own audience. */
export type Audience = "public" | "friends" | "private";
export type ContactKind = "email" | "phone" | "website" | "instagram" | "tiktok" | "facebook" | "linkedin" | "x" | "youtube" | "github" | "telegram" | "other";
export interface Contact {
  id: string;
  kind: ContactKind;
  value: string;
  label: string;
  visibility: Audience;
  href?: string | null;
}

/** Image editor state; replayed to re-edit a photo or background. */
export interface Crop {
  zoom: number;
  x: number;
  y: number;
  rotation: 0 | 90 | 180 | 270;
  brightness: number;
  contrast: number;
  saturation: number;
}

export interface PersonCard {
  id: string;
  handle: string;
  display_name: string;
  bio: string;
  avatar_url: string;
  cover_url: string;
}

export interface Friendship {
  id: string;
  status: "pending" | "accepted";
  direction: "incoming" | "outgoing";
  user: PersonCard;
  created_at: string;
}

export interface AILimit {
  scope: "tenant" | "user" | "app";
  period: "month" | "day";
  app_id: string | null;
  limit_usd: number;
  spent_usd: number;
  remaining_usd: number;
  used_pct: number;
}

export interface AIOverview {
  period_start: string;
  tier: string;
  /** The person's Omni plan (ADR-0033) */
  plan?: { id: string; name: string } | null;
  /** AI is off until they choose an Omni plan */
  plan_required?: boolean;
  allowed: boolean;
  reason: string;
  limits: AILimit[];
  spent_usd: number;
  apps: { app_id: string | null; spent_usd: number; requests: number; limit: AILimit | null }[];
}

export function usd(value: number | null | undefined): string {
  if (value == null) return "unlimited";
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "USD", maximumFractionDigits: value < 1 ? 4 : 2 }).format(value);
}

export interface PublicProfile {
  id: string;
  handle: string;
  display_name: string;
  bio: string;
  avatar_url: string;
  cover_url: string;
  contacts: Contact[];
  is_owner: boolean;
  can_connect: boolean;
  connection: { id: string; status: "pending" | "accepted"; direction: "incoming" | "outgoing" } | null;
  member_since: string;
}

export interface Discount { percent: number; ends_at: string | null; note: string }

export interface Plan {
  id: string;
  name: string;
  /** What this person pays (after any discount, ADR-0033) */
  price: number;
  /** The price before the discount; null when there's none */
  list_price?: number | null;
  discount?: Discount | null;
  currency: string;
  interval: "month" | "year";
  trial_days: number;
  features: string[];
}

export interface Subscription {
  id: string;
  app_id: string;
  app_name?: string;
  plan_id: string;
  price: number;
  list_price?: number | null;
  currency: string;
  interval: string;
  status: string;
  provider: string;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
}

export interface InstallState {
  plan_id: string;
  status: string;
  entitled: boolean;
  reason: string;
  subscription: Subscription | null;
}

export interface AppCard {
  id: string;
  name: string;
  version: string;
  description: string;
  developer: string;
  category: string;
  color: string;
  icon_url: string | null;
  icon_background?: string | null;
  widgets?: { id: string; title: string; description: string; sizes: string[]; default_size: string | null }[];
  plans: Plan[];
  installed?: boolean;
  installation?: InstallState | null;
}

export interface AppDetail extends AppCard {
  permissions: { id: string; description: string }[];
  collections: { name: string; description: string; shared: boolean }[];
  source: string;
}

export interface Invite {
  id: string;
  code: string;
  url: string;
  email: string | null;
  note: string;
  max_uses: number;
  uses: number;
  revoked: boolean;
  expires_at: string | null;
  created_at: string;
}

export function formatPrice(minor: number, currency: string, interval?: string): string {
  if (minor === 0) return "Free";
  const amount = new Intl.NumberFormat(undefined, { style: "currency", currency }).format(minor / 100);
  return interval ? `${amount} / ${interval === "year" ? "yr" : "mo"}` : amount;
}
